import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Typography, makeStyles } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import ErrorIcon from "@material-ui/icons/Error";
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle";
import { Link } from 'react-router-dom';
import { getQueryParams } from '../../../utils/fns/get-query-params';
import { AtCircularProgress } from '../../../components/AtCircularProgress';

// Static color map — never changes, no need to recreate per render
const COLOR_STATUS = {
  uptime: {
    color: '#10B981',
    bgColor: '#F0FDF4',
    textColor: '#15803D',
  },
  downtime: {
    color: '#EF4444',
    bgColor: '#FEF2F2',
    textColor: '#B91C1C',
  },
  partial: {
    color: '#F59E0B',
    bgColor: '#FFFBEB',
    textColor: '#B45309',
  },
  noMonitoring: {
    color: '#9CA3AF',
    bgColor: '#F3F4F6',
    textColor: '#4B5563',
  }
};

const useStyles = makeStyles((theme) => ({
  statusCircle: {
    position: 'relative',
    width: '180px',
    height: '180px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: '24px'
  },
  statusIndicator: {
    textAlign: 'center',
  },
  summaryBox: {
    padding: '12px 8px',
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flex: 1,
    minWidth: '100px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    }
  }
}));

const SystemHealth = ({ salud, loading }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  if (loading || !salud || salud.total === 0) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" style={{ height: '100%' }}>
          <AtCircularProgress />
        </Box>
    );
  }

  const totalMonitored = (salud.totalUptime || 0) + (salud.totalDowntime || 0) + (salud.totalPartialtime || 0);

  const statusConfigs = {
    uptime: {
      key: 'uptime',
      label: t('home.systemHealth.healthy', { defaultValue: 'Sano' }),
      count: salud.totalUptime,
      value: totalMonitored > 0 ? (salud.totalUptime / totalMonitored) * 100 : 0,
      icon: <CheckCircleIcon style={{ color: COLOR_STATUS.uptime.color, fontSize: '40px' }} />,
      smallIcon: <CheckCircleIcon style={{ color: COLOR_STATUS.uptime.color, fontSize: '14px' }} />,
      color: COLOR_STATUS.uptime.color,
      subText: t('home.systemHealth.healthySubtext', { count: salud.totalUptime, defaultValue: `({{count}} objetivos sin problemas)` }),
      bgColor: COLOR_STATUS.uptime.bgColor,
      textColor: COLOR_STATUS.uptime.textColor,
    },
    downtime: {
      key: 'downtime',
      label: t('home.systemHealth.critical', { defaultValue: 'Crítico' }),
      count: salud.totalDowntime,
      value: totalMonitored > 0 ? (salud.totalDowntime / totalMonitored) * 100 : 0,
      icon: <CancelIcon style={{ color: COLOR_STATUS.downtime.color, fontSize: '40px' }} />,
      smallIcon: <CancelIcon style={{ color: COLOR_STATUS.downtime.color, fontSize: '14px' }} />,
      color: COLOR_STATUS.downtime.color,
      subText: t('home.systemHealth.criticalSubtext', { count: salud.totalDowntime, defaultValue: `({{count}} objetivos con fallas críticas)` }),
      bgColor: COLOR_STATUS.downtime.bgColor,
      textColor: COLOR_STATUS.downtime.textColor,
    },
    partial: {
      key: 'partial',
      label: t('home.systemHealth.atRisk', { defaultValue: 'En riesgo' }),
      count: salud.totalPartialtime,
      value: totalMonitored > 0 ? (salud.totalPartialtime / totalMonitored) * 100 : 0,
      icon: <ErrorIcon style={{ color: COLOR_STATUS.partial.color, fontSize: '40px' }} />,
      smallIcon: <ErrorIcon style={{ color: COLOR_STATUS.partial.color, fontSize: '14px' }} />,
      color: COLOR_STATUS.partial.color,
      subText: t('home.systemHealth.atRiskSubtext', { count: salud.totalPartialtime, defaultValue: `({{count}} objetivos presentan latencia)` }),
      bgColor: COLOR_STATUS.partial.bgColor,
      textColor: COLOR_STATUS.partial.textColor,
    }
  };
  
  const current = statusConfigs.uptime;
  const items = [statusConfigs.uptime, statusConfigs.downtime, statusConfigs.partial];

  return (
        <Box display="flex" alignItems="center" flexWrap="wrap" gridGap="24px" flex={1}>
          <Box className={classes.statusCircle}>
            <CircularProgress
              variant="determinate"
              value={current.value || 0}
              size={180}
              thickness={4}
              style={{ color: current.color, position: 'absolute', zIndex: 1 }}
            />
            <CircularProgress
              variant="determinate"
              value={100}
              size={180}
              thickness={4}
              style={{ color: '#E5E7EB', position: 'absolute'}}
            />
            <Box className={classes.statusIndicator}>
              {current.icon}
              <Typography style={{ color: current.color, fontWeight: 700, fontSize: '1.1rem', marginTop: '8px' }}>
                {current.label}
              </Typography>
            </Box>
          </Box>

          <Box flex={1} minWidth={260}>
            <Typography variant="body2" style={{ color: '#6B7280', marginBottom: '4px' }}>
              {t('home.systemHealth.predominantStatus', { defaultValue: 'Estado Predominante:' })}
            </Typography>

            <Typography variant="body1" style={{ fontWeight: 700, color: '#111827', marginBottom: '24px', fontSize: '1.1rem' }}>
              {current.label} <span style={{ fontWeight: 400, color: '#6B7280', display: 'block', fontSize: '0.95rem', marginTop: '4px' }}>
                {current.subText}
              </span>
            </Typography>

            <Box
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gridGap="12px"
              width="100%"
            >
              {items.map((item) => (
                <Link
                  key={item.key}
                  to={{
                    pathname: `/quickview`,
                    search: getQueryParams(),
                  }}
                  style={{
                    flex: 1,
                    textDecoration: 'none',
                    display: 'flex',
                  }}
                >
                  <Box
                    className={classes.summaryBox}
                    style={{
                      backgroundColor: item.bgColor,
                      border: '2px solid transparent',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap="8px" mb={1}>
                      {item.smallIcon}

                      <Typography
                        variant="caption"
                        style={{
                          color: item.textColor,
                          fontWeight: 700,
                          fontSize: '0.8rem',
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>

                    <Typography
                      variant="h4"
                      style={{
                        color: '#111827',
                        fontWeight: 800,
                      }}
                    >
                      {item.count}
                    </Typography>
                  </Box>
                </Link>
              ))}
            </Box>
          </Box>
          <Box
            className={classes.summaryBox}
            style={{
              backgroundColor: '#EFF6FF',
              position: 'absolute',
              top: '24px',
              right: '24px',
              minWidth: 'auto',
              padding: '8px 16px',
              alignItems:'center'
            }}
          >
            <Typography variant="caption" style={{ color: '#1E40AF', fontWeight: 500 }}>
              {t('home.systemHealth.totalObjectives', { defaultValue: 'Total objetivos' })}
            </Typography>
            <Box display="flex" style={{ gap: '6px', whiteSpace: 'nowrap', alignItems:'center' }}>
              <Typography variant="h5" style={{ color: '#111827', fontWeight: 700 }}>
                {salud.total + (salud.totalNoMonitoring || 0)}
              </Typography>
              {salud.totalNoMonitoring > 0 && (
                <Typography variant="caption" style={{ color: '#6B7280', fontWeight: 500, fontSize: '0.75rem' }}>
                  {t('home.systemHealth.unmonitoredSubtext', { count: salud.totalNoMonitoring, defaultValue: `({{count}} sin monitoreo)` })}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
  );
};

export default SystemHealth;